import { useCallback, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { StoreId } from "@KassaroAB/shared/types/ids";
import type { ChatSession, ChatSessionMessage } from "@KassaroAB/shared/types/chatSession";
import {
  streamCreateStoreChatSession,
  streamPostStoreChatSessionMessage,
  formatStreamChatError,
  type AgentProgressStep,
} from "../api/storeChatSessionsStreamApi";
import {
  storeChatSessionChangeRequestQueryKey,
  storeChatSessionMessagesQueryKey,
  storeChatSessionsQueryKey,
} from "../query/chatSessionsQuery";

export const NEW_CHAT_STREAM_KEY = "__new__";

type StreamSlice = {
  isStreaming: boolean;
  progressSteps: AgentProgressStep[];
  streamMessages: ChatSessionMessage[];
  error: Error | null;
};

type StreamHandle = {
  alive: boolean;
  key: string;
  messages: ChatSessionMessage[];
};

const emptySlice = (): StreamSlice => ({
  isStreaming: false,
  progressSteps: [],
  streamMessages: [],
  error: null,
});

const appendUniqueMessage = (
  messages: ChatSessionMessage[],
  message: ChatSessionMessage,
): ChatSessionMessage[] => {
  if (message.id !== undefined && messages.some((existing) => existing.id === message.id)) {
    return messages;
  }
  return [...messages, message];
};

export const useStreamChatSession = (
  storeId: StoreId | undefined,
  activeKey: string | null,
) => {
  const queryClient = useQueryClient();
  const [slices, setSlices] = useState<Record<string, StreamSlice>>({});
  const [createdSessions, setCreatedSessions] = useState<ChatSession[]>([]);
  const liveStreamsRef = useRef(new Map<string, StreamHandle>());

  const activeSlice = activeKey !== null ? (slices[activeKey] ?? emptySlice()) : emptySlice();

  const beginStream = useCallback((key: string): StreamHandle => {
    const previous = liveStreamsRef.current.get(key);
    if (previous !== undefined) {
      previous.alive = false;
    }
    const handle: StreamHandle = { alive: true, key, messages: [] };
    liveStreamsRef.current.set(key, handle);
    return handle;
  }, []);

  const rebindStream = useCallback((fromKey: string, toKey: string, handle: StreamHandle) => {
    liveStreamsRef.current.delete(fromKey);
    const previous = liveStreamsRef.current.get(toKey);
    if (previous !== undefined && previous !== handle) {
      previous.alive = false;
    }
    handle.key = toKey;
    liveStreamsRef.current.set(toKey, handle);
  }, []);

  const patchSlice = useCallback(
    (key: string, updater: (previous: StreamSlice) => StreamSlice) => {
      setSlices((previousSlices) => {
        const current = previousSlices[key] ?? emptySlice();
        return {
          ...previousSlices,
          [key]: updater(current),
        };
      });
    },
    [],
  );

  const renameSlice = useCallback((fromKey: string, toKey: string) => {
    setSlices((previousSlices) => {
      const moving = previousSlices[fromKey];
      if (moving === undefined) {
        return previousSlices;
      }
      const nextSlices = { ...previousSlices };
      delete nextSlices[fromKey];
      nextSlices[toKey] = moving;
      return nextSlices;
    });
  }, []);

  const upsertSessionInQueryCache = useCallback(
    (session: ChatSession) => {
      if (!storeId) {
        return;
      }
      queryClient.setQueryData<ChatSession[]>(
        storeChatSessionsQueryKey(storeId),
        (currentSessions) => {
          const sessions = currentSessions ?? [];
          const existingIndex = sessions.findIndex((existing) => existing.id === session.id);
          if (existingIndex === -1) {
            return [session, ...sessions];
          }
          const nextSessions = [...sessions];
          nextSessions[existingIndex] = session;
          return nextSessions;
        },
      );
    },
    [queryClient, storeId],
  );

  const rememberCreatedSession = useCallback((session: ChatSession) => {
    setCreatedSessions((previousSessions) => {
      const existingIndex = previousSessions.findIndex((existing) => existing.id === session.id);
      if (existingIndex === -1) {
        return [session, ...previousSessions];
      }
      const nextSessions = [...previousSessions];
      nextSessions[existingIndex] = session;
      return nextSessions;
    });
  }, []);

  const appendMessageToQueryCache = useCallback(
    (chatSessionId: string, message: ChatSessionMessage) => {
      if (!storeId) {
        return;
      }
      queryClient.setQueryData<ChatSessionMessage[]>(
        storeChatSessionMessagesQueryKey(storeId, chatSessionId),
        (currentMessages) => appendUniqueMessage(currentMessages ?? [], message),
      );
    },
    [queryClient, storeId],
  );

  const seedMessagesQueryCache = useCallback(
    (chatSessionId: string, messages: ChatSessionMessage[]) => {
      if (!storeId || messages.length === 0) {
        return;
      }
      queryClient.setQueryData<ChatSessionMessage[]>(
        storeChatSessionMessagesQueryKey(storeId, chatSessionId),
        (currentMessages) => {
          return messages.reduce(
            (merged, message) => appendUniqueMessage(merged, message),
            currentMessages ?? [],
          );
        },
      );
    },
    [queryClient, storeId],
  );

  const invalidateSessionQueries = useCallback(
    (chatSessionId: string) => {
      if (!storeId) {
        return;
      }
      queryClient.invalidateQueries({
        queryKey: storeChatSessionMessagesQueryKey(storeId, chatSessionId),
      });
      queryClient.invalidateQueries({
        queryKey: storeChatSessionChangeRequestQueryKey(storeId, chatSessionId),
      });
      queryClient.invalidateQueries({ queryKey: storeChatSessionsQueryKey(storeId) });
    },
    [queryClient, storeId],
  );

  const addProgressStep = useCallback(
    (key: string, step: AgentProgressStep) => {
      patchSlice(key, (previous) => {
        const alreadyRecorded = previous.progressSteps.some(
          (existingStep) => existingStep.tool === step.tool && existingStep.phase === step.phase,
        );
        if (alreadyRecorded) {
          return previous;
        }
        return {
          ...previous,
          progressSteps: [...previous.progressSteps, step],
        };
      });
    },
    [patchSlice],
  );

  const sendMessage = useCallback(
    async (chatSessionId: string, message: string, files?: File[]) => {
      if (!storeId) {
        throw new Error("No store");
      }

      const handle = beginStream(chatSessionId);
      patchSlice(chatSessionId, (previous) => ({
        isStreaming: true,
        progressSteps: [],
        streamMessages: previous.streamMessages,
        error: null,
      }));

      return streamPostStoreChatSessionMessage(
        storeId,
        chatSessionId,
        message,
        files,
        {
          onProgress: (step) => {
            if (!handle.alive) {
              return;
            }
            addProgressStep(handle.key, step);
          },
          onUserMessage: (userMessage) => {
            if (!handle.alive) {
              return;
            }
            handle.messages = appendUniqueMessage(handle.messages, userMessage);
            patchSlice(handle.key, (previous) => ({
              ...previous,
              streamMessages: appendUniqueMessage(previous.streamMessages, userMessage),
            }));
            appendMessageToQueryCache(handle.key, userMessage);
          },
          onAssistantMessage: (assistantMessage) => {
            if (!handle.alive) {
              return;
            }
            handle.messages = appendUniqueMessage(handle.messages, assistantMessage);
            patchSlice(handle.key, (previous) => ({
              ...previous,
              streamMessages: appendUniqueMessage(previous.streamMessages, assistantMessage),
            }));
            appendMessageToQueryCache(handle.key, assistantMessage);
          },
          onError: (errorMessage) => {
            if (!handle.alive) {
              return;
            }
            patchSlice(handle.key, (previous) => ({
              ...previous,
              error: new Error(errorMessage),
            }));
          },
        },
      )
        .then(() => {
          if (!handle.alive) {
            return;
          }
          invalidateSessionQueries(chatSessionId);
        })
        .catch((streamError: unknown) => {
          if (!handle.alive) {
            return;
          }
          const nextError = formatStreamChatError(streamError);
          patchSlice(handle.key, (previous) => ({
            ...previous,
            error: nextError,
          }));
          throw nextError;
        })
        .finally(() => {
          if (!handle.alive) {
            return;
          }
          patchSlice(handle.key, (previous) => ({
            ...previous,
            isStreaming: false,
            progressSteps: [],
          }));
        });
    },
    [
      storeId,
      beginStream,
      patchSlice,
      addProgressStep,
      appendMessageToQueryCache,
      invalidateSessionQueries,
    ],
  );

  const createSession = useCallback(
    async (
      message: string,
      files: File[] | undefined,
      onSessionCreated?: (session: ChatSession) => void,
    ): Promise<ChatSession | null> => {
      if (!storeId) {
        throw new Error("No store");
      }

      const handle = beginStream(NEW_CHAT_STREAM_KEY);
      patchSlice(NEW_CHAT_STREAM_KEY, () => ({
        isStreaming: true,
        progressSteps: [],
        streamMessages: [],
        error: null,
      }));

      let createdChatSession: ChatSession | null = null;

      return streamCreateStoreChatSession(storeId, message, files, {
        onProgress: (step) => {
          if (!handle.alive) {
            return;
          }
          addProgressStep(handle.key, step);
        },
        onUserMessage: (userMessage) => {
          if (!handle.alive) {
            return;
          }
          handle.messages = appendUniqueMessage(handle.messages, userMessage);
          patchSlice(handle.key, (previous) => ({
            ...previous,
            streamMessages: appendUniqueMessage(previous.streamMessages, userMessage),
          }));
          if (handle.key !== NEW_CHAT_STREAM_KEY) {
            appendMessageToQueryCache(handle.key, userMessage);
          }
        },
        onAssistantMessage: (assistantMessage) => {
          if (!handle.alive) {
            return;
          }
          handle.messages = appendUniqueMessage(handle.messages, assistantMessage);
          patchSlice(handle.key, (previous) => ({
            ...previous,
            streamMessages: appendUniqueMessage(previous.streamMessages, assistantMessage),
          }));
          if (handle.key !== NEW_CHAT_STREAM_KEY) {
            appendMessageToQueryCache(handle.key, assistantMessage);
          }
        },
        onSession: (session) => {
          if (!handle.alive) {
            return;
          }
          createdChatSession = session;
          rememberCreatedSession(session);
          upsertSessionInQueryCache(session);
          seedMessagesQueryCache(session.id, handle.messages);
          if (handle.key !== NEW_CHAT_STREAM_KEY) {
            return;
          }
          renameSlice(NEW_CHAT_STREAM_KEY, session.id);
          rebindStream(NEW_CHAT_STREAM_KEY, session.id, handle);
          onSessionCreated?.(session);
        },
        onError: (errorMessage) => {
          if (!handle.alive) {
            return;
          }
          patchSlice(handle.key, (previous) => ({
            ...previous,
            error: new Error(errorMessage),
          }));
        },
      })
        .then(() => {
          if (!handle.alive) {
            return null;
          }
          if (createdChatSession !== null) {
            rememberCreatedSession(createdChatSession);
            upsertSessionInQueryCache(createdChatSession);
            invalidateSessionQueries(createdChatSession.id);
          } else {
            queryClient.invalidateQueries({ queryKey: storeChatSessionsQueryKey(storeId) });
          }
          return createdChatSession;
        })
        .catch((streamError: unknown) => {
          if (!handle.alive) {
            return null;
          }
          const nextError = formatStreamChatError(streamError);
          patchSlice(handle.key, (previous) => ({
            ...previous,
            error: nextError,
          }));
          throw nextError;
        })
        .finally(() => {
          if (!handle.alive) {
            return;
          }
          patchSlice(handle.key, (previous) => ({
            ...previous,
            isStreaming: false,
            progressSteps: [],
          }));
        });
    },
    [
      storeId,
      beginStream,
      patchSlice,
      addProgressStep,
      appendMessageToQueryCache,
      rememberCreatedSession,
      upsertSessionInQueryCache,
      seedMessagesQueryCache,
      renameSlice,
      rebindStream,
      invalidateSessionQueries,
      queryClient,
    ],
  );

  return {
    sendMessage,
    createSession,
    isStreaming: activeSlice.isStreaming,
    progressSteps: activeSlice.progressSteps,
    streamMessages: activeSlice.streamMessages,
    createdSessions,
    isPendingNewChat: slices[NEW_CHAT_STREAM_KEY]?.isStreaming === true,
    error: activeSlice.error,
  };
};
